import { FieldInfo } from '@/components/FieldInfo';
import type { OrgFormApi } from '@/config/organizations/formTypes';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import orgStyles from '../../styles.module.css';
import { gridProps, stackProps } from '../constants';
import styles from '../styles.module.css';

type BasicInfoSectionProps = {
  form: OrgFormApi;
};

function BasicInfoSectionComponent({ form }: BasicInfoSectionProps) {
  const { t } = useTranslation('orgs');

  if (!form || typeof (form as any).Field !== 'function') {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[BasicInfoSectionComponent] form prop is missing or invalid:', form);
    }
    return null;
  }

  return (
    <Stack {...stackProps}>
      <Text as="h3" className={orgStyles.sectionHeading}>
        {t('create_org_section_basic')}
      </Text>

      <Grid className={styles.basicInfoSectionGrid} {...gridProps}>
        <Stack {...stackProps} gap={10}>
          {/* Name Field */}
          <form.Field
            name="name"
            validators={{
              onMount: ({ value }) => (!value?.trim() ? { message: t('validation_name_required') } : undefined),
              onChange: ({ value }) => (!value?.trim() ? { message: t('validation_name_required') } : undefined),
            }}
          >
            {field => (
              <>
                <Input
                  autoComplete="off"
                  className={orgStyles.uiInput}
                  data-testid="input-name"
                  id="name"
                  label={{
                    children: t('create_org_name_label'),
                    htmlFor: 'name',
                    id: 'label-name',
                  }}
                  name="name"
                  value={field.state.value}
                  onChange={e => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t('create_org_name_placeholder')}
                />
                <FieldInfo field={field} />
              </>
            )}
          </form.Field>

          {/* Alias Field */}
          <form.Field name="alias">
            {field => (
              <>
                <Input
                  autoComplete="off"
                  className={orgStyles.uiInput}
                  data-testid="input-alias"
                  id="alias"
                  label={{
                    children: t('create_org_alias_label'),
                    htmlFor: 'alias',
                    id: 'label-alias',
                  }}
                  name="alias"
                  value={field.state.value ?? ''}
                  onChange={e => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t('create_org_alias_placeholder')}
                />
                <FieldInfo field={field} />
              </>
            )}
          </form.Field>
        </Stack>

        {/* Address Field */}
        <form.Field name="address">
          {field => (
            <>
              <Textarea
                autoComplete="off"
                className={orgStyles.uiInput}
                data-testid="input-address"
                id="address"
                label={{
                  children: t('create_org_address_label'),
                  htmlFor: 'address',
                  id: 'label-address',
                }}
                name="address"
                value={field.state.value ?? ''}
                onChange={e => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder={t('create_org_address_placeholder')}
                rows={7}
              />
              <FieldInfo field={field} />
            </>
          )}
        </form.Field>
      </Grid>
    </Stack>
  );
}

export const BasicInfoSection = memo(BasicInfoSectionComponent);
